import React, { useState, useEffect, useCallback, useRef } from 'react';

// --- ӨГӨГДӨЛ ---
const INITIAL_REQUESTS = [
  { id: 1, name: "Батболд", plate: "95--УБМ", address: "1105-02" },
  { id: 2, name: "Гантөмөр", plate: "15--УБУ", address: "1106-15" },
  { id: 3, name: "Цэлмэг", plate: "15--УБК", address: "1115-44" },
  { id: 4, name: "Д.Цолмон", plate: "68--УНЦ", address: "1125-43" },
];

const INITIAL_CARS = [
  { id: 101, build: "Болд", plate: "13--УЕУ", status: false },
  { id: 102, build: "Бат", plate: "56--УКА", status: true },
  { id: 103, build: "Содбат", plate: "80--УАО", status: false },
];

const DashboardMockup = () => {
  const [requests, setRequests] = useState(INITIAL_REQUESTS);
  const [cars, setCars] = useState(INITIAL_CARS);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  // --- АНИМАЦИЙН ЛОГИК ---
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  const triggerAccept = useCallback((id) => {
    setRequests(prev => prev.map(r => r.id === id ? { ...r, animating: 'accept' } : r));
    setTimeout(() => {
      setRequests(prev => {
        const req = prev.find(r => r.id === id);
        if (req) {
          const newCar = { id: req.plate, build: req.address, plate: req.plate, status: true, animating: 'enter' };
          setCars(c => c.some(car => car.plate === newCar.plate) ? c : [newCar, ...c]);
          setTimeout(() => setCars(c => c.map(car => car.id === newCar.id ? { ...car, animating: '' } : car)), 500);
        }
        return prev.filter(r => r.id !== id);
      });
    }, 400);
  }, []);

  const triggerReject = useCallback((id) => {
    setRequests(prev => prev.map(r => r.id === id ? { ...r, animating: 'reject' } : r));
    setTimeout(() => setRequests(prev => prev.filter(r => r.id !== id)), 400);
  }, []);

  // --- AUTO DEMO LOOP ---
  useEffect(() => {
    let isMounted = true;
    const runAutoDemo = async () => {
      while (isMounted) {
        setRequests(INITIAL_REQUESTS); setCars(INITIAL_CARS);
        await delay(2000); if (!isMounted) break; triggerAccept(1);
        await delay(2500); if (!isMounted) break; triggerReject(2);
        await delay(2500); if (!isMounted) break; triggerAccept(3);
        await delay(5000);
      }
    };
    runAutoDemo();
    return () => { isMounted = false; };
  }, [triggerAccept, triggerReject]);

  // --- RESPONSIVE SCALE LOGIC ---
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.offsetWidth;
        const designWidth = 1100;
        if (parentWidth < designWidth) {
          setScale(parentWidth / designWidth);
        } else {
          setScale(1);
        }
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div ref={containerRef} className="tw-w-full tw-flex tw-justify-center tw-items-center tw-overflow-hidden">
      
      <div 
        className="tw-relative tw-bg-[#f4f7fb] tw-rounded-[30px] md:tw-rounded-[50px] tw-shadow-2xl tw-border tw-border-slate-200/60 tw-flex tw-font-sans tw-overflow-hidden"
        style={{ 
          width: '1100px', 
          height: '650px',
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          margin: `calc(${(650 * scale - 650) / 2}px) calc(${(1100 * scale - 1100) / 2}px)`
        }}
      >
        
        {/* --- SIDEBAR (ОЛОН ЦЭСТЭЙ БОЛСОН ХЭСЭГ) --- */}
        <div className="tw-w-[80px] tw-bg-[#0f172a] tw-flex tw-flex-col tw-justify-between tw-py-8 tw-shrink-0 tw-items-center tw-border-r tw-border-slate-800">
          
          <div className="tw-flex tw-flex-col tw-items-center tw-w-full">
            {/* Logo */}
            <div className="tw-w-12 tw-h-12 tw-bg-emerald-500 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-2xl tw-shadow-[0_0_15px_rgba(16,185,129,0.5)] tw-mb-8">P</div>
            
            <div className="tw-flex tw-flex-col tw-w-full tw-space-y-3">
              {/* Menu 1: Home / Dashboard */}
              <div className="tw-w-full tw-flex tw-justify-center tw-py-3 tw-cursor-pointer tw-text-slate-500 hover:tw-text-white tw-transition-colors">
                <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
              </div>

              {/* Menu 2: Users / Residents */}
              <div className="tw-w-full tw-flex tw-justify-center tw-py-3 tw-cursor-pointer tw-text-slate-500 hover:tw-text-white tw-transition-colors">
                 <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
              </div>

              {/* Menu 3: ACTIVE (Parking Gates & Requests) */}
              <div className="tw-w-full tw-relative tw-flex tw-justify-center tw-py-3 tw-cursor-pointer tw-text-emerald-400 tw-bg-emerald-900/20">
                <div className="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-[3px] tw-bg-emerald-500 tw-rounded-r-md"></div>
                <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>
              </div>

              {/* Menu 4: Payments / Billing */}
              <div className="tw-w-full tw-flex tw-justify-center tw-py-3 tw-cursor-pointer tw-text-slate-500 hover:tw-text-white tw-transition-colors">
                <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>

              {/* Menu 5: Reports / Analytics */}
              <div className="tw-w-full tw-flex tw-justify-center tw-py-3 tw-cursor-pointer tw-text-slate-500 hover:tw-text-white tw-transition-colors">
                <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-w-full tw-space-y-4">
            {/* Menu 6: Settings */}
            <div className="tw-w-full tw-flex tw-justify-center tw-py-2 tw-cursor-pointer tw-text-slate-500 hover:tw-text-white tw-transition-colors">
               <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            </div>
            {/* Menu 7: Logout */}
            <div className="tw-w-full tw-flex tw-justify-center tw-py-2 tw-cursor-pointer tw-text-slate-500 hover:tw-text-red-400 tw-transition-colors">
               <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
            </div>
          </div>
        </div>

        {/* --- MAIN CONTENT (Side-by-Side) --- */}
        <div className="tw-flex-1 tw-flex tw-flex-row tw-p-8 tw-gap-6 tw-bg-[#f8fafc] tw-relative">
          
          <div className="tw-absolute tw-top-0 tw-right-12 tw-bg-red-500 tw-text-white tw-px-5 tw-py-1.5 tw-rounded-b-2xl tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-shadow-md tw-z-50 tw-flex tw-items-center">
             <span className="tw-w-2 tw-h-2 tw-bg-white tw-rounded-full tw-animate-pulse tw-mr-2"></span>
             LIVE DEMO
          </div>

          {/* LEFT: REQUESTS */}
          <div className="tw-flex-1 tw-flex tw-flex-col tw-bg-white tw-rounded-[32px] tw-shadow-sm tw-border tw-border-slate-200/80 tw-overflow-hidden">
            <div className="tw-p-6 tw-border-b tw-border-slate-100 tw-flex tw-justify-between tw-items-center tw-bg-slate-50/50">
              <h2 className="tw-text-xl tw-font-black tw-text-slate-800">АПП ХҮСЭЛТҮҮД</h2>
              <div className="tw-bg-blue-600 tw-text-white tw-text-xs tw-font-bold tw-px-3 tw-py-1 tw-rounded-full">{requests.length}</div>
            </div>
            <div className="tw-flex-1 tw-p-4 tw-space-y-4 tw-overflow-y-auto">
              {requests.map((req) => (
                <div key={req.id} className={`tw-bg-white tw-rounded-2xl tw-p-5 tw-border tw-flex tw-justify-between tw-items-center tw-transition-all tw-duration-500 ${req.animating === 'accept' ? 'tw-bg-green-50 tw-border-green-300 tw-opacity-0 tw-translate-x-12' : req.animating === 'reject' ? 'tw-bg-red-50 tw-border-red-300 tw-opacity-0 tw-scale-90' : 'tw-border-slate-100'}`}>
                  <div className="tw-flex tw-items-center">
                    <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-indigo-50 tw-text-indigo-600 tw-font-bold tw-flex tw-items-center tw-justify-center tw-mr-4 tw-text-lg">{req.name.charAt(0)}</div>
                    <div>
                      <h4 className="tw-font-bold tw-text-slate-800 tw-text-base">{req.name}</h4>
                      <p className="tw-text-sm tw-text-slate-500">{req.plate}</p>
                    </div>
                  </div>
                  <div className="tw-flex tw-items-center tw-space-x-3">
                    <div className="tw-bg-slate-100 tw-text-slate-800 tw-font-bold tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-sm">{req.plate}</div>
                    <div className="tw-w-6 tw-h-9 tw-bg-red-600 tw-text-white tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-text-small tw-text-sm">X</div>
                    <div className="tw-w-24 tw-h-9 tw-bg-blue-600 tw-text-white tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-text-small tw-font-bold tw-text-sm">Зөвшөөрөх</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: REGISTERED CARS */}
          <div className="tw-flex-1 tw-flex tw-flex-col tw-bg-white tw-rounded-[32px] tw-shadow-sm tw-border tw-border-slate-200/80 tw-overflow-hidden">
            <div className="tw-p-6 tw-border-b tw-border-slate-100 tw-flex tw-justify-between tw-items-center tw-bg-slate-50/50">
              <h2 className="tw-text-xl tw-font-black tw-text-slate-800">БҮРТГЭЛТЭЙ</h2>
              <div className="tw-text-emerald-500 tw-font-black tw-text-xs">+ НЭМЭХ</div>
            </div>
            <div className="tw-flex-1 tw-overflow-hidden">
              <table className="tw-w-full">
                <thead>
                  <tr className="tw-bg-slate-50 tw-text-slate-400 tw-text-xs tw-font-bold">
                    <th className="tw-py-4 tw-px-6 tw-text-left">УЛСЫН ДУГААР</th>
                    <th className="tw-py-4 tw-px-6 tw-text-left">БАЙР</th>
                    <th className="tw-py-4 tw-px-6 tw-text-right">ТӨЛӨВ</th>
                  </tr>
                </thead>
                <tbody className="tw-text-slate-700">
                  {cars.map((row) => (
                    <tr key={row.id} className={`tw-border-b tw-border-slate-50 tw-transition-all tw-duration-700 ${row.animating === 'enter' ? 'tw-bg-green-50 tw-translate-y-4' : ''}`}>
                      <td className="tw-py-5 tw-px-6"><span className="tw-bg-slate-100 tw-px-3 tw-py-1 tw-rounded-lg tw-font-black">{row.plate}</span></td>
                      <td className="tw-py-5 tw-px-6 tw-font-bold tw-text-slate-500">{row.build}</td>
                      <td className="tw-py-5 tw-px-6">
                        <div className={`tw-ml-auto tw-w-10 tw-h-5 tw-rounded-full tw-relative tw-flex tw-items-center tw-px-1 ${row.status ? 'tw-bg-emerald-500' : 'tw-bg-slate-300'}`}>
                          <div className="tw-w-3.5 tw-h-3.5 tw-bg-white tw-rounded-full tw-ml-auto"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMockup;